/**
 * AI Market Hub - Главная страница
 * Премиальный Landing Page с современным дизайном
 * Автор: AI Market Hub Team
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import MagneticButton from '../components/MagneticButton';
import FloatingParticles from '../components/FloatingParticles';
import AnimatedCard from '../components/AnimatedCard';
import GlowingEffect from '../components/GlowingEffect';
import ShinyButton from '../components/ShinyButton';
import GlassPanel from '../components/GlassPanel';
import BorderGradient from '../components/BorderGradient';
import { HighlightText } from '../components/ui/AnimatedRevealText.jsx';
import { GlowingEffectCard } from '../components/ui/GlowingEffectCard.jsx';
import { showToast, initToast } from '../components/Toast';
import { initAllScrollEffects } from '../utils/scrollReveal.js';
import { API_URL } from '../utils/api.js';
import { http } from '../utils/httpClient.js';
import { supabase, authHelpers } from '../lib/supabase.js';
import { formatErrorMessage } from '../utils/formatErrorMessage.js';

// ===== КОНФИГУРАЦИЯ СОЦИАЛЬНЫХ ССЫЛОК =====
const SOCIAL_LINKS = {
  youtube: { url: 'https://www.youtube.com/@AiMarketHub', label: 'YouTube', icon: '🎬' },
  telegramGroup: { url: 'https://t.me/ai_n8n_hub', label: 'Telegram группа', icon: '💬' },
  telegramChannel: { url: 'https://t.me/N8N_automatisation', label: 'Telegram канал', icon: '📢' },
  developer: { url: 'https://t.me/solvillage', label: 'Разработчик', icon: '👨‍💻' },
  cooperation: { url: 'https://t.me/solvillage', label: 'Сотрудничество', icon: '🤝' },
  dzen: { url: 'https://dzen.ru/aimarkethub', label: 'Дзен', icon: '📰' },
  threads: { url: 'https://www.threads.com/@aimarkethub_club', label: 'Threads', icon: '🧵' },
  vk: { url: 'https://vk.com/aimarkethub', label: 'ВКонтакте', icon: '💙' },
  secondSite: { url: 'https://aimarkethub.pro/', label: 'AI Market Hub Pro', icon: '🚀' },
};

// ===== ДАННЫЕ ДЛЯ КОНТЕНТА =====
const FEATURES = [
  {
    icon: '🎬',
    title: 'Загрузка видео',
    description: 'Загружайте до 10 видео одновременно. Поддержка всех популярных форматов: MP4, MOV, AVI, MKV и других.',
    gradient: 'from-red-500 to-orange-500',
  },
  {
    icon: '🎤',
    title: 'AI Транскрибация',
    description: 'Мощный Whisper AI распознаёт речь с точностью 98%. Поддержка русского, английского и ещё 50+ языков.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: '✨',
    title: 'Генерация контента',
    description: 'GPT-4 и Gemini создают цепляющие названия, SEO-описания и хэштеги. Уникальный контент за секунды.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: '⏰',
    title: 'Умные таймкоды',
    description: 'Автоматическое создание навигации по видео. AI определяет ключевые моменты и темы.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: '📱',
    title: 'Telegram интеграция',
    description: 'Публикуйте в каналы одним кликом. Красивое форматирование, превью видео, умные хэштеги.',
    gradient: 'from-sky-500 to-blue-600',
  },
  {
    icon: '🚀',
    title: 'YouTube публикация',
    description: 'Прямая загрузка на ваш канал. Автозаполнение всех полей, планирование публикаций.',
    gradient: 'from-rose-500 to-red-600',
  },
];

const STATS = [
  { value: '50K+', label: 'Видео обработано', icon: '🎬' },
  { value: '98%', label: 'Точность распознавания', icon: '🎯' },
  { value: '24/7', label: 'Доступность сервиса', icon: '⏰' },
  { value: '0₽', label: 'Стоимость', icon: '💎' },
  { value: '50+', label: 'Языков поддержки', icon: '🌍' },
  { value: '5+', label: 'Часов экономии в неделю', icon: '⚡' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Загрузите видео',
    description: 'Перетащите файлы или выберите из галереи. Поддерживается загрузка до 10 видео за раз.',
    icon: '📤',
    color: 'from-blue-600 to-indigo-600',
  },
  {
    step: '02',
    title: 'AI обрабатывает',
    description: 'Whisper транскрибирует речь, GPT генерирует название, описание и таймкоды.',
    icon: '🤖',
    color: '', // Убрано, используется bg-white/5
  },
  {
    step: '03',
    title: 'Публикуйте',
    description: 'Отправьте готовый контент на YouTube и в Telegram каналы одним кликом.',
    icon: '🚀',
    color: 'from-emerald-600 to-teal-600',
  },
];

const USE_CASES = [
  {
    title: 'Для YouTube блогеров',
    description: 'Экономьте 3-4 часа на каждом видео. AI создаёт SEO-оптимизированные описания, которые повышают охваты.',
    icon: '🎥',
    benefits: ['Больше просмотров', 'Меньше рутины', 'SEO-оптимизация'],
  },
  {
    title: 'Для владельцев каналов',
    description: 'Управляйте контентом нескольких каналов из одного места. Планируйте публикации заранее.',
    icon: '📺',
    benefits: ['Мультиканальность', 'Планирование', 'Аналитика'],
  },
  {
    title: 'Для маркетологов',
    description: 'Создавайте контент для разных платформ за минуты. Кросс-постинг в Telegram, YouTube, и другие соцсети.',
    icon: '📊',
    benefits: ['Кросс-постинг', 'Экономия времени', 'Единый стиль'],
  },
  {
    title: 'Для образования',
    description: 'Конвертируйте лекции и вебинары в текст. Создавайте субтитры и конспекты автоматически.',
    icon: '🎓',
    benefits: ['Субтитры', 'Конспекты', 'Доступность'],
  },
];

const FAQ = [
  {
    question: 'Сервис действительно бесплатный?',
    answer: 'Да! AI Market Hub полностью бесплатен. Мы используем собственные серверы для обработки видео и не берём плату за базовый функционал.',
  },
  {
    question: 'Какие форматы видео поддерживаются?',
    answer: 'Поддерживаются все популярные форматы: MP4, MOV, AVI, MKV, WebM, FLV и другие. Максимальный размер файла — 2 ГБ.',
  },
  {
    question: 'Насколько точная транскрибация?',
    answer: 'Мы используем Whisper от OpenAI — одну из лучших моделей распознавания речи. Точность для чистой речи достигает 98%. Поддерживается 50+ языков.',
  },
  {
    question: 'Как быстро обрабатывается видео?',
    answer: 'Среднее время обработки — 1-3 минуты на каждые 10 минут видео. Зависит от качества аудио и нагрузки на серверы.',
  },
  {
    question: 'Безопасно ли загружать видео?',
    answer: 'Абсолютно. Все видео хранятся на защищённых серверах и автоматически удаляются после обработки. Мы не передаём данные третьим лицам.',
  },
  {
    question: 'Нужна ли регистрация?',
    answer: 'Да, для использования сервиса нужно создать бесплатный аккаунт. Это позволяет сохранять историю обработки, настройки и лимиты использования. Регистрация занимает менее минуты и не требует подтверждения email (но рекомендуется для восстановления доступа).',
  },
  {
    question: 'Какие есть лимиты использования?',
    answer: 'Базовые лимиты: 3 видео в день для загрузки, 3 транскрибации в день, 4 генерации описаний в день, 4 публикации в Telegram в день. Максимум 3 видео одновременно в хранилище. Видео автоматически удаляются через 3 дня. Все лимиты обновляются ежедневно в 00:00 по московскому времени.',
  },
  {
    question: 'Можно ли использовать сервис для коммерческих целей?',
    answer: 'Да, абсолютно! AI Market Hub можно использовать для любых целей, включая коммерческие. Мы не ограничиваем использование сервиса. Единственное требование — соблюдение лимитов использования.',
  },
  {
    question: 'Какие AI модели используются?',
    answer: 'Для транскрибации: Whisper от OpenAI (локальная версия), Groq Whisper API, OpenAI Whisper API. Для генерации описаний: GPT-4, GPT-4o-mini, Gemini Pro, Claude 3.5 Sonnet через OpenRouter. Вы можете выбрать модель в настройках.',
  },
  {
    question: 'Поддерживается ли клиентская транскрибация?',
    answer: 'Да! Мы поддерживаем клиентскую транскрибацию через WebGPU/WASM с использованием Transformers.js. Это позволяет обрабатывать видео прямо в браузере без отправки на сервер, что быстрее и безопаснее. Требуется современный браузер с поддержкой WebGPU.',
  },
  {
    question: 'Как работает автопубликация на YouTube?',
    answer: 'После обработки видео вы можете одним кликом опубликовать его на YouTube. Система автоматически заполняет название, описание, теги и таймкоды. Вам нужно только авторизоваться через OAuth и выбрать канал. Поддерживается планирование публикаций.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Алексей К.',
    role: 'YouTube блогер, 150K подписчиков',
    text: 'Раньше на описание одного видео уходило 30-40 минут. Теперь — 2 минуты. AI Market Hub реально изменил мой workflow.',
    avatar: '👨‍💼',
    rating: 5,
  },
  {
    name: 'Мария С.',
    role: 'SMM-менеджер',
    text: 'Веду 5 каналов для клиентов. Этот сервис экономит мне 15+ часов в неделю. Особенно нравится кросс-постинг в Telegram.',
    avatar: '👩‍💻',
    rating: 5,
  },
  {
    name: 'Дмитрий П.',
    role: 'Онлайн-преподаватель',
    text: 'Использую для создания субтитров к лекциям. Точность распознавания впечатляет. И всё это бесплатно!',
    avatar: '👨‍🏫',
    rating: 5,
  },
];

const ROADMAP = [
  {
    category: '🌐 Мультиплатформенность',
    items: ['TikTok автопостинг', 'Instagram Reels', 'YouTube Shorts', 'Twitter/X интеграция', 'Pinterest'],
    status: 'soon',
  },
  {
    category: '🤖 AI нового поколения',
    items: ['Генерация превью (DALL-E)', 'Автонарезка на highlights', 'Голосовое озвучивание', 'Перевод на 100+ языков'],
    status: 'development',
  },
  {
    category: '📊 Аналитика',
    items: ['SEO-анализ заголовков', 'Прогноз виральности', 'A/B тестирование', 'Интеграция с YouTube Analytics'],
    status: 'planned',
  },
  {
    category: '⚡ Автоматизация',
    items: ['Контент-календарь', 'Telegram бот управления', 'Автопостинг по расписанию', 'Webhooks и API'],
    status: 'planned',
  },
];

// ===== КОМПОНЕНТЫ =====

// Анимированный фон с частицами (удален, используется BackgroundPaths)
function AnimatedBackground() {
  return null;
}

// Счётчик с анимацией
function AnimatedCounter({ value, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const numericValue = parseInt(value.replace(/\D/g, '')) || 0;
  const suffix = value.replace(/[\d]/g, '');
  
  useEffect(() => {
    let start = 0;
    const end = numericValue;
    const incrementTime = duration / end;
    
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, Math.max(incrementTime, 10));
    
    return () => clearInterval(timer);
  }, [numericValue, duration]);
  
  return <span>{count}{suffix}</span>;
}

// Карточка функции с GlowingEffect
function FeatureCard({ feature, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      className="group relative min-h-[280px]"
    >
      <div className="relative h-full rounded-[1.5rem] border-[0.75px] border-white/10 p-2">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
          borderWidth={2}
          variant="default"
        />
        <div className="relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl border-[0.75px] border-white/5 bg-white/[0.02] backdrop-blur-sm p-6 md:p-8 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
          {/* Иконка */}
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl md:text-3xl group-hover:scale-110 transition-transform duration-300">
            {feature.icon}
          </div>
          
          {/* Контент */}
          <div className="flex-1 flex flex-col justify-between gap-3">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 tracking-tight">
                {feature.title}
              </h3>
              <p className="text-slate-400 text-sm md:text-base leading-relaxed tracking-tight">
                {feature.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// FAQ аккордеон
function FAQItem({ item, isOpen, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border-b border-white/10 last:border-none"
    >
      <button
        onClick={onClick}
        className="w-full py-5 md:py-6 flex items-center justify-between text-left group"
      >
        <span className="text-base md:text-lg font-semibold text-white group-hover:text-slate-300 transition-colors pr-4 tracking-tight">
          {item.question}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-slate-400 text-xl flex-shrink-0"
        >
          ▼
        </motion.span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 md:pb-6 text-slate-400 text-sm md:text-base leading-relaxed tracking-tight">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Модальное окно (улучшенный дизайн)
function Modal({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
      onClick={onClose}
    >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl"
          >
            {/* Свечение по краям */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl opacity-20 blur-xl pointer-events-none" />
        
        {/* Закрыть кнопка */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-white rounded-full hover:bg-white/10 transition-all z-10"
        >
          ✕
        </button>
        
        {/* Контент */}
        <div className="relative z-10">
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ===== ГЛАВНЫЙ КОМПОНЕНТ =====
export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Форма регистрации
  const [registerData, setRegisterData] = useState({ email: '', name: '', password: '', confirmPassword: '' });
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false); // Успешная регистрация (письмо отправлено)
  const [showPassword, setShowPassword] = useState(false);

  // Форма входа
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Состояния для сброса пароля
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // Состояния для установки нового пароля (после сброса)
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({ password: '', confirmPassword: '' });
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPasswordField, setShowResetPasswordField] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState(''); // Email пользователя для формы сброса
  
  // Генерация пароля
  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setRegisterData({ ...registerData, password: newPassword, confirmPassword: newPassword });
    setShowPassword(true); // Показываем пароль чтобы пользователь мог его скопировать
  };

  // Инициализация Toast и Scroll Effects
  useEffect(() => {
    initToast();
    // Инициализируем scroll reveal эффекты
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        initAllScrollEffects();
      }, 100);
    }
  }, []);

  // Проверка авторизации (не редиректим если идёт сброс пароля)
  useEffect(() => {
    const showResetPasswordParam = searchParams.get('showResetPassword');
    // Не редиректим если открыто окно сброса пароля
    if (showResetPasswordParam === 'true') return;
    
    const token = localStorage.getItem('session_token');
    if (token) navigate('/youtube');
  }, [navigate, searchParams]);

  // Состояние для отображения успешного подтверждения email
  const [emailConfirmedSuccess, setEmailConfirmedSuccess] = useState(false);

  // Обработка параметров URL для открытия модальных окон
  useEffect(() => {
    const showLoginParam = searchParams.get('showLogin');
    const showResetPasswordParam = searchParams.get('showResetPassword');
    const showForgotPasswordParam = searchParams.get('showForgotPassword');
    const emailConfirmed = searchParams.get('emailConfirmed');

    if (showLoginParam === 'true') {
      setShowLogin(true);
      if (emailConfirmed === 'true') {
        // Показываем сообщение об успешном подтверждении email
        setLoginError('');
        setEmailConfirmedSuccess(true);
        // Убираем сообщение через 5 секунд
        setTimeout(() => setEmailConfirmedSuccess(false), 5000);
      }
      // Убираем параметры из URL
      setSearchParams({});
    }

    if (showResetPasswordParam === 'true') {
      setShowResetPassword(true);
      // Получаем email из Supabase сессии
      if (supabase) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user?.email) {
            setResetPasswordEmail(session.user.email);
          }
        });
      }
      // Очищаем флаг password_recovery_flow если был
      sessionStorage.removeItem('password_recovery_flow');
      // Убираем параметры из URL
      setSearchParams({});
    }
    
    // Проверяем флаг из sessionStorage (устанавливается в main.jsx при PASSWORD_RECOVERY)
    const recoveryFlow = sessionStorage.getItem('password_recovery_flow');
    if (recoveryFlow === 'true') {
      setShowResetPassword(true);
      // Получаем email из Supabase сессии
      if (supabase) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user?.email) {
            setResetPasswordEmail(session.user.email);
          }
        });
      }
      // Очищаем флаг
      sessionStorage.removeItem('password_recovery_flow');
    }

    if (showForgotPasswordParam === 'true') {
      setShowLogin(true);
      setShowForgotPasswordForm(true);
      // Убираем параметры из URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Отслеживание скролла для хедера
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Обработка регистрации
  const handleRegister = async (e) => {
    e?.preventDefault();
    setRegisterError('');
    
    if (!registerData.email || !registerData.name || !registerData.password) {
      setRegisterError('Заполните все поля');
      return;
    }
    if (registerData.password.length < 8) {
      setRegisterError('Пароль минимум 8 символов');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('Пароли не совпадают');
      return;
    }
    
    setRegisterLoading(true);
    console.log('[Register] Отправка запроса...');
    console.log('[Register] Данные:', { email: registerData.email, name: registerData.name });

    const completeLegacyRegistration = (response) => {
      if (response.ok && response.data?.success) {
        localStorage.setItem('session_token', response.data.sessionToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.removeItem('youtube_settings_draft');
        axios.defaults.headers.common['x-session-token'] = response.data.sessionToken;
        setShowRegister(false);
        navigate('/youtube');
        return true;
      }
      return false;
    };
    
    try {
      // Сначала пробуем Supabase Auth (приоритет)
      if (supabase) {
        let supabaseFailed = false;

        try {
          const result = await authHelpers.signUp(
            registerData.email,
            registerData.password,
            { 
              name: registerData.name,
              redirect_to: 'https://aimarkethub.ru/auth/callback'
            }
          );
          
          console.log('[Register] Результат signUp:', result);
          
          if (result?.error) {
            supabaseFailed = true;
            throw result.error;
          }
          
          const data = result?.data || result;
          
          if (!data) {
            console.error('[Register] data is null/undefined, result:', result);
            throw new Error('Не получен ответ от сервера. Попробуйте ещё раз.');
          }

          // Тихая повторная регистрация Supabase (email уже есть)
          if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
            setRegisterError('Этот email уже зарегистрирован. Войдите или используйте другой email.');
            return;
          }
          
          if (data.user) {
            try {
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              const { data: { session } } = await supabase.auth.getSession();
              if (!session?.access_token) {
                console.log('[Register] Сессия Supabase не найдена - требуется подтверждение email');
                setRegisterSuccess(true);
                return;
              }
              
              console.log('[Register] Сессия Supabase получена, запрашиваем пользователя из backend...');
              
              let userResponse = null;
              let attempts = 0;
              const maxAttempts = 5;
              
              while (attempts < maxAttempts) {
                try {
                  userResponse = await http.get('/user/me');
                  if (userResponse.ok && userResponse.data?.user) {
                    break;
                  }
                } catch (err) {
                  console.log(`[Register] Попытка ${attempts + 1}/${maxAttempts} получить пользователя...`);
                }
                
                attempts++;
                if (attempts < maxAttempts) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }
              
              if (userResponse?.ok && userResponse.data?.user) {
                localStorage.setItem('user', JSON.stringify(userResponse.data.user));
                sessionStorage.removeItem('youtube_settings_draft');
                setShowRegister(false);
                navigate('/youtube');
                return;
              }

              console.log('[Register] Требуется подтверждение email');
              setRegisterSuccess(true);
              return;
            } catch (err) {
              console.error('[Register] Ошибка получения пользователя из backend:', err);
              setRegisterSuccess(true);
              return;
            }
          }

          console.log('[Register] Регистрация прошла успешно, требуется подтверждение email');
          setRegisterSuccess(true);
          return;
        } catch (supabaseError) {
          console.warn('[Register] Supabase signUp не удался, пробуем legacy API:', supabaseError);

          const legacyResponse = await http.post('/user/register', {
            email: registerData.email,
            name: registerData.name,
            password: registerData.password
          });

          if (completeLegacyRegistration(legacyResponse)) {
            return;
          }

          if (supabaseFailed) {
            setRegisterError(formatErrorMessage(supabaseError, 'Ошибка регистрации'));
            return;
          }

          setRegisterError(formatErrorMessage(
            legacyResponse.data?.error || legacyResponse.error,
            'Ошибка регистрации'
          ));
          return;
        }
      }
      
      // Fallback на старую систему (если Supabase не настроен)
      const response = await http.post('/user/register', {
        email: registerData.email,
        name: registerData.name,
        password: registerData.password
      });
      
      console.log('[Register] Ответ:', response);
      
      if (completeLegacyRegistration(response)) {
        return;
      }

      const errorMsg = formatErrorMessage(response.data?.error || response.error, 'Ошибка регистрации');
      console.error('[Register] Ошибка:', errorMsg);
      
      if (typeof errorMsg === 'string' && (errorMsg.toLowerCase().includes('email') || errorMsg.toLowerCase().includes('существует'))) {
        setRegisterError('Этот email уже зарегистрирован. Войдите или используйте другой email.');
      } else {
        setRegisterError(errorMsg);
      }
    } catch (error) {
      console.error('[Register] Исключение:', error);
      setRegisterError(formatErrorMessage(error, 'Ошибка регистрации'));
    } finally {
      setRegisterLoading(false);
    }
  };

  // Обработка сброса пароля
  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);
    
    if (!forgotPasswordEmail) {
      setForgotPasswordError('Введите email');
      return;
    }
    
    setForgotPasswordLoading(true);
    
    try {
      if (supabase) {
        // Перенаправляем на страницу установки нового пароля
        await authHelpers.resetPassword(forgotPasswordEmail, 'https://aimarkethub.ru/auth/callback?type=recovery');
        setForgotPasswordSuccess(true);
      } else {
        throw new Error('Supabase не настроен');
      }
    } catch (error) {
      console.error('[ForgotPassword] Ошибка:', error);
      setForgotPasswordError(formatErrorMessage(error, 'Ошибка отправки письма'));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Обработка установки нового пароля (после сброса)
  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setResetPasswordError('');
    setResetPasswordSuccess(false);
    
    if (!resetPasswordData.password || !resetPasswordData.confirmPassword) {
      setResetPasswordError('Заполните все поля');
      return;
    }
    
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      setResetPasswordError('Пароли не совпадают');
      return;
    }
    
    if (resetPasswordData.password.length < 8) {
      setResetPasswordError('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    setResetPasswordLoading(true);
    
    try {
      if (supabase) {
        // Обновляем пароль через Supabase
        const { error } = await supabase.auth.updateUser({
          password: resetPasswordData.password
        });
        
        if (error) {
          throw error;
        }
        
        setResetPasswordSuccess(true);
        
        // Через 2 секунды автоматически входим в личный кабинет
        setTimeout(async () => {
          try {
            // Получаем данные пользователя из бэкенда для сохранения в localStorage
            const { http } = await import('../utils/httpClient.js');
            const userResponse = await http.get('/user/me');
            
            if (userResponse.ok && userResponse.data?.user) {
              localStorage.setItem('user', JSON.stringify(userResponse.data.user));
              // Очищаем черновики настроек YouTube при входе после сброса пароля
              sessionStorage.removeItem('youtube_settings_draft');
              console.log('[ResetPassword] Очищены черновики YouTube настроек при входе после сброса пароля');
            }
          } catch (err) {
            console.error('[ResetPassword] Ошибка получения данных пользователя:', err);
          }
          
          setShowResetPassword(false);
          setResetPasswordData({ password: '', confirmPassword: '' });
          setResetPasswordEmail('');
          // Перенаправляем в личный кабинет
          navigate('/youtube');
        }, 2000);
        
      } else {
        throw new Error('Supabase не настроен');
      }
    } catch (error) {
      console.error('[ResetPassword] Ошибка:', error);
      let errorMessage = error.message || 'Ошибка установки пароля';
      if (errorMessage.includes('session')) {
        errorMessage = 'Ссылка для сброса пароля истекла. Запросите новую ссылку.';
      }
      setResetPasswordError(errorMessage);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Обработка входа
  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginError('');
    
    if (!loginData.email || !loginData.password) {
      setLoginError('Заполните все поля');
      return;
    }
    
    setLoginLoading(true);
    console.log('[Login] Отправка запроса...');

    const completeLegacyLogin = (response) => {
      if (response.ok && response.data?.success) {
        localStorage.setItem('session_token', response.data.sessionToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.removeItem('youtube_settings_draft');
        axios.defaults.headers.common['x-session-token'] = response.data.sessionToken;
        setShowLogin(false);
        navigate('/youtube');
        return true;
      }
      return false;
    };

    const tryLegacyLogin = async () => {
      const response = await http.post('/user/login', {
        email: loginData.email,
        password: loginData.password
      });
      console.log('[Login] Legacy ответ:', response);
      return response;
    };
    
    try {
      if (supabase) {
        const result = await authHelpers.signIn(loginData.email, loginData.password);
        
        console.log('[Login] Результат signIn:', result);
        
        if (result?.error) {
          const errorMessage = formatErrorMessage(result.error, 'Неверный email или пароль');
          const errorText = `${result.error?.message || ''} ${result.error?.code || ''} ${result.error?.name || ''}`;
          const shouldTryLegacy = /invalid login credentials|invalid credentials|user not found|fetch failed|failed to fetch|network|retryable/i.test(errorText);

          if (shouldTryLegacy) {
            const legacyResponse = await tryLegacyLogin();
            if (completeLegacyLogin(legacyResponse)) {
              return;
            }
            setLoginError(formatErrorMessage(
              legacyResponse.data?.error || legacyResponse.error,
              errorMessage
            ));
            return;
          }

          setLoginError(errorMessage);
          return;
        }
        
        const data = result?.data || result;
        
        if (data?.user) {
          try {
            const userResponse = await http.get('/user/me');
            
            if (userResponse.ok && userResponse.data?.user) {
              localStorage.setItem('user', JSON.stringify(userResponse.data.user));
              sessionStorage.removeItem('youtube_settings_draft');
              setShowLogin(false);
              navigate('/youtube');
              return;
            }

            const legacyResponse = await tryLegacyLogin();
            if (completeLegacyLogin(legacyResponse)) {
              return;
            }

            setLoginError(formatErrorMessage(
              userResponse.data?.error || userResponse.error,
              'Не удалось получить данные пользователя'
            ));
            return;
          } catch (err) {
            console.error('[Login] Ошибка получения пользователя из backend:', err);

            const legacyResponse = await tryLegacyLogin();
            if (completeLegacyLogin(legacyResponse)) {
              return;
            }

            setLoginError(formatErrorMessage(err, 'Ошибка входа. Попробуйте ещё раз.'));
            return;
          }
        }

        const legacyResponse = await tryLegacyLogin();
        if (completeLegacyLogin(legacyResponse)) {
          return;
        }
        setLoginError(formatErrorMessage(
          legacyResponse.data?.error || legacyResponse.error,
          'Неверный email или пароль'
        ));
        return;
      }
      
      const response = await tryLegacyLogin();
      
      if (completeLegacyLogin(response)) {
        return;
      }

      setLoginError(formatErrorMessage(
        response.data?.error || response.error,
        'Неверный email или пароль'
      ));
    } catch (error) {
      console.error('[Login] Исключение:', error);
      if (!navigator.onLine) {
        setLoginError('Нет подключения к интернету');
      } else {
        setLoginError(formatErrorMessage(error, 'Ошибка соединения'));
      }
    } finally {
      setLoginLoading(false);
    }
  };
  

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden relative">
      {/* Background Particles */}
      <FloatingParticles />
      
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-900/20 opacity-40 blur-3xl rounded-full" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-900/20 opacity-30 blur-3xl rounded-full" />
      </div>
      
      {/* ===== HEADER ===== */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#050505]/80 backdrop-blur-md transition-all duration-300`}
        style={{ paddingTop: 'max(env(safe-area-inset-top), 44px)' }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Логотип */}
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-500/10 border border-purple-500/20 text-purple-500">
              <span className="text-lg">🚀</span>
            </div>
            <span className="text-base tracking-tight text-white uppercase font-display hidden sm:block">
              AI Market Hub
            </span>
          </motion.div>

          {/* Навигация (desktop) */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-400">
            {[
              { href: '#features', label: 'Возможности' },
              { href: '#how-it-works', label: 'Как работает' },
              { href: '#faq', label: 'FAQ' },
              { href: '#contacts', label: 'Контакты' },
            ].map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                className="hover:text-white transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.querySelector(link.href);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Кнопки */}
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowLogin(true)}
              className="hidden sm:block px-5 py-2.5 text-neutral-400 hover:text-white font-medium transition-colors text-base"
            >
              Войти
            </motion.button>
            <ShinyButton
              onClick={() => setShowRegister(true)}
              variant="green"
              size="sm"
            >
              <span className="flex items-center gap-2">
                Начать бесплатно
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </span>
            </ShinyButton>
              
              {/* Мобильное меню */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
          </div>
        </div>

        {/* Мобильное меню dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-[#08080c]/95 backdrop-blur-xl border-t border-white/5"
            >
              <nav className="px-4 py-4 space-y-2">
                {[
                  { href: '#features', label: 'Возможности' },
                  { href: '#how-it-works', label: 'Как работает' },
                  { href: '#faq', label: 'FAQ' },
                  { href: '#contacts', label: 'Контакты' },
                ].map((link) => (
                  <a 
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 px-4 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors tracking-tight"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="pt-2 border-t border-white/10">
                  <button
                    onClick={() => { setShowLogin(true); setMobileMenuOpen(false); }}
                    className="w-full py-3 px-4 text-left text-white hover:bg-white/5 rounded-lg font-medium tracking-tight"
                  >
                    🔐 Войти
                  </button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-32 px-6 z-10 overflow-hidden" style={{ paddingTop: 'max(calc(env(safe-area-inset-top) + 10rem), 10rem)' }}>
        {/* Background Gradients */}
        <div className="absolute -translate-x-1/2 blur-[100px] pointer-events-none -z-10 bg-accent-900/20 w-[1000px] h-[400px] rounded-full top-0 left-1/2"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col text-center items-center">
            {/* Badge */}
            <div className="animate-on-scroll mb-6 inline-flex items-center gap-2 rounded-full border border-accent-500/20 bg-accent-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-accent-300 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-500"></span>
              </span>
              100% Бесплатно • Без ограничений • Поддержка 50+ языков
            </div>

            {/* Title */}
            <h1 className="animate-on-scroll stagger-delay-1 font-display font-geist text-5xl sm:text-6xl lg:text-7xl leading-[1.1] tracking-tighter mb-6 text-white">
              Где <span className="text-accent-300">автоматизация</span> <br className="hidden sm:block" />
              <span className="text-white">становится</span> <br className="hidden sm:block" />
              <span className="text-accent-300">реальностью.</span>
            </h1>

            {/* Description */}
            <p className="animate-on-scroll stagger-delay-2 text-base sm:text-lg leading-relaxed text-neutral-400 tracking-tight mb-10 max-w-2xl mx-auto">
              Загружайте видео → Whisper AI транскрибирует с точностью 98% → GPT-4 генерирует SEO-описания → 
              Автоматическая публикация на YouTube и Telegram. Экономьте 5+ часов в неделю!
            </p>

            {/* CTA Buttons */}
            <div className="animate-on-scroll stagger-delay-3 flex flex-col sm:flex-row gap-6 items-center justify-center">
              <ShinyButton
                onClick={() => setShowRegister(true)}
                variant="green"
                size="md"
              >
                <span className="flex items-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.21 1.21 0 0 0 1.72 0L21.64 5.36a1.21 1.21 0 0 0 0-1.72Z"></path>
                    <path d="M19 14v4"></path>
                    <path d="M21 16h-4"></path>
                  </svg>
                  Начать бесплатно
                </span>
              </ShinyButton>

              <a 
                href="#how-it-works"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-full p-[1px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]"
              >
                <span className="absolute inset-[-100%] animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0%,transparent_75%,#ffffff_100%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
                <span className="absolute inset-0 rounded-full bg-white/10 transition-opacity duration-300 group-hover:opacity-0"></span>
                <span className="relative flex h-full w-full items-center justify-center gap-2 rounded-full bg-gradient-to-b from-neutral-900 to-black px-5 py-2.5 text-sm font-medium text-neutral-400 transition-colors duration-300 group-hover:text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
                  Как это работает
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:translate-x-1">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </span>
              </a>
            </div>
          </div>

          {/* Статистика */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 max-w-6xl mx-auto mt-20 animate-on-scroll">
            {STATS.map((stat, i) => (
              <GlassPanel
                key={i}
                className={`p-4 md:p-6 rounded-2xl text-center ${i > 0 ? `stagger-delay-${Math.min(i, 3)}` : ''}`}
              >
                <div className="text-2xl md:text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 font-geist">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-neutral-400 tracking-tight">{stat.label}</div>
              </GlassPanel>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section id="features" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 md:flex md:items-end md:justify-between animate-on-scroll">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] ring-1 uppercase tracking-tight font-medium bg-accent-400/10 text-accent-300 ring-accent-300/20 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                  <path d="M12 22v-5"></path>
                  <path d="M9 8V2"></path>
                  <path d="M15 8V2"></path>
                  <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"></path>
                </svg>
                Возможности
              </span>
              <HighlightText
                text="Всё для автоматизации контента"
                as="h2"
                className="mb-4"
                textClassName="font-display font-geist text-3xl md:text-4xl text-white tracking-tight"
                highlightColor="#22c55e"
                duration={1.8}
                ease="easeOut"
              />
              <p className="text-neutral-400 font-light">
                Мощные AI-инструменты, которые экономят часы работы каждый день. Точность 98%, поддержка 50+ языков.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-on-scroll">
            {FEATURES.map((feature, i) => (
              <div key={i} className="relative min-h-[280px] group">
                <div className="relative h-full rounded-[1.5rem] border-[0.75px] border-white/10 p-2">
                  <GlowingEffectCard
                    spread={40}
                    glow={true}
                    disabled={false}
                    proximity={64}
                    inactiveZone={0.01}
                    borderWidth={2}
                    variant="default"
                  />
                  <div className="relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl border-[0.75px] border-white/5 bg-white/[0.02] backdrop-blur-sm p-6 md:p-8 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
                    {/* Иконка */}
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl md:text-3xl group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    
                    {/* Контент */}
                    <div className="flex-1 flex flex-col justify-between gap-3">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 tracking-tight font-geist">
                          {feature.title}
                        </h3>
                        <p className="text-slate-400 text-sm md:text-base leading-relaxed tracking-tight">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-32 px-6 relative overflow-hidden bg-neutral-900/50 border-t border-white/5">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-accent-500/50 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 animate-on-scroll text-center">
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] ring-1 uppercase tracking-tight font-medium bg-accent-400/10 text-accent-300 ring-accent-300/20 mb-4">
              Процесс
            </span>
            <h2 className="font-display font-geist text-4xl sm:text-5xl tracking-tight text-white mb-4">
              Как это <span className="text-white">работает</span>
            </h2>
            <p className="text-neutral-400 font-light mt-4 max-w-2xl mx-auto">
              От загрузки видео до публикации — за 5 минут. Простой процесс, мощный результат.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-on-scroll">
            {HOW_IT_WORKS.map((step, i) => (
              <GlassPanel key={i} className={`p-10 rounded-2xl border-l-2 border-l-accent-500 border-t-0 border-r-0 border-b-0 ${i > 0 ? `stagger-delay-${Math.min(i, 3)}` : ''}`}>
                <div className="mb-6 inline-flex rounded-lg border border-accent-500/20 bg-accent-500/10 p-2.5 text-accent-400 text-2xl">
                  {step.icon}
                </div>
                <div className="text-xs font-mono text-accent-400 mb-2 uppercase tracking-wider">Шаг {step.step}</div>
                <h3 className="text-xl font-medium text-white mb-2 font-geist">{step.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </GlassPanel>
            ))}
          </div>
        </div>
      </section>

      {/* ===== USE CASES ===== */}
      <section className="py-24 bg-[#050505] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="font-display text-4xl mb-6 tracking-tight text-white">
              Для кого этот сервис
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {USE_CASES.map((useCase, i) => (
              <AnimatedCard key={i} className="flex gap-4 p-4 rounded-xl border border-transparent hover:border-purple-500/30 transition-colors bg-white/5">
                <div className="shrink-0 w-10 h-10 rounded bg-purple-900/20 flex items-center justify-center text-purple-400 text-2xl">
                  {useCase.icon}
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">{useCase.title}</h4>
                  <p className="text-sm text-gray-400 mb-3">{useCase.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {useCase.benefits.map((benefit, j) => (
                      <span key={j} className="px-2 py-1 bg-purple-900/20 border border-purple-500/20 rounded text-xs text-purple-300">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h2 className="font-display text-3xl md:text-5xl mb-4 text-white">
            Что говорят пользователи
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Реальные отзывы от тех, кто уже использует AI Market Hub
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, i) => (
            <AnimatedCard key={i} className="p-8 rounded-2xl bg-white/5">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, j) => (
                  <span key={j} className="text-yellow-500">⭐</span>
                ))}
              </div>
              <p className="text-gray-300 font-light italic mb-6 text-sm">
                "{testimonial.text}"
              </p>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-neutral-800 border border-white/10 flex items-center justify-center text-lg">
                  {testimonial.avatar}
                </div>
                <div>
                  <div className="text-white font-medium text-xs">{testimonial.name}</div>
                  <div className="text-xs text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            </AnimatedCard>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none"></div>
        
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="mb-16 animate-on-scroll text-center">
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] ring-1 uppercase tracking-tight font-medium bg-accent-400/10 text-accent-300 ring-accent-300/20 mb-4">
              FAQ
            </span>
            <h2 className="font-display font-geist text-4xl sm:text-5xl tracking-tight text-white mb-4">
              Частые <span className="text-white">вопросы</span>
            </h2>
            <p className="text-neutral-400 font-light mt-4">
              Всё, что вам нужно знать о AI Market Hub
            </p>
          </div>

          <GlassPanel className="rounded-3xl p-6 md:p-8 animate-on-scroll">
            {FAQ.map((item, i) => (
              <FAQItem 
                key={i} 
                item={item} 
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
              />
            ))}
          </GlassPanel>
        </div>
      </section>

      {/* ===== ROADMAP ===== */}
      <section className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="font-display text-4xl tracking-tight text-white">
              Планы развития
            </h2>
            <p className="text-gray-400 font-light mt-4">
              Мы постоянно работаем над новыми функциями
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROADMAP.map((item, i) => (
              <AnimatedCard key={i} className="p-8 rounded-2xl bg-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{item.category}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-mono tracking-wider ${
                    item.status === 'soon' ? 'bg-purple-500/20 border border-purple-500/30 text-purple-400' :
                    item.status === 'development' ? 'bg-white/5 border border-white/10 text-gray-300' :
                    'bg-white/5 border border-white/5 text-gray-400'
                  }`}>
                    {item.status === 'soon' ? 'СКОРО' : item.status === 'development' ? 'В РАБОТЕ' : 'ПЛАН'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {item.items.map((feature, j) => (
                    <li key={j} className="text-gray-400 text-sm flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">→</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-32 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[300px] bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.15),_transparent_70%)] opacity-30 blur-3xl pointer-events-none"></div>

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="animate-on-scroll font-display font-geist text-4xl sm:text-5xl md:text-6xl mb-6 tracking-tight text-white">
            Готовы автоматизировать <span className="heading-gradient">контент?</span>
          </h2>
          <p className="animate-on-scroll stagger-delay-1 text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
            Присоединяйтесь к тысячам пользователей, которые уже экономят 5+ часов в неделю с AI Market Hub. Бесплатно, быстро, без ограничений.
          </p>
          <div className="animate-on-scroll stagger-delay-2 flex flex-col items-center gap-6">
            <ShinyButton
              onClick={() => setShowRegister(true)}
              variant="green"
              size="lg"
            >
              <span className="flex items-center gap-2">
                Создать аккаунт бесплатно
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </span>
            </ShinyButton>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer id="contacts" className="border-t border-white/10 bg-[#000000] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-500/10 border border-purple-500/20 text-purple-500">
                  <span className="text-lg">🚀</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                  AI Market Hub
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-6 max-w-md">
                Бесплатный сервис для автоматизации YouTube и Telegram. 
                AI транскрибация, генерация описаний, публикация — всё в одном месте.
              </p>
              
              {/* Социальные иконки */}
              <div className="flex flex-wrap gap-3">
                {Object.entries(SOCIAL_LINKS).slice(0, 6).map(([key, link]) => (
                  <a
                    key={key}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center justify-center text-lg transition-all hover:scale-110 hover:border-purple-500/30"
                    title={link.label}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Навигация и Ресурсы */}
            <div className="flex gap-12 text-sm text-gray-500 font-mono tracking-wider uppercase">
              <div className="flex flex-col gap-3">
                <span className="text-white">Платформа</span>
                <a href="#features" className="hover:text-purple-400 transition-colors">Возможности</a>
                <a href="#how-it-works" className="hover:text-purple-400 transition-colors">Как работает</a>
                <a href="#faq" className="hover:text-purple-400 transition-colors">FAQ</a>
              </div>
              <div className="flex flex-col gap-3">
                <span className="text-white">Ресурсы</span>
                <a href={SOCIAL_LINKS.youtube.url} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">YouTube</a>
                <a href={SOCIAL_LINKS.telegramGroup.url} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">Telegram</a>
                <a href={SOCIAL_LINKS.developer.url} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">Контакты</a>
              </div>
            </div>
          </div>
          
          <div className="mt-20 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-[10px] text-gray-700 font-mono uppercase">
            <span>© {new Date().getFullYear()} AI Market Hub. Все права защищены.</span>
            <span className="mt-2 md:mt-0">Бесплатно • Без ограничений</span>
          </div>
        </div>
      </footer>

      {/* ===== МОДАЛЬНЫЕ ОКНА ===== */}
      <AnimatePresence>
        {/* Регистрация */}
        {showRegister && (
          <Modal onClose={() => { 
            setShowRegister(false); 
            setRegisterSuccess(false);
            setRegisterError('');
            setRegisterData({ email: '', name: '', password: '', confirmPassword: '' });
          }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {registerSuccess ? '📧 Проверьте почту' : 'Создать аккаунт'}
            </h2>
            <p className="text-gray-400 mb-6">
              {registerSuccess ? 'Письмо с подтверждением отправлено' : 'Бесплатно и без ограничений'}
            </p>
            
            {registerSuccess ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <p className="text-green-400 text-sm mb-2">
                    ✅ Письмо отправлено на <strong>{registerData.email}</strong>
                  </p>
                  <p className="text-gray-400 text-sm">
                    Нажмите на ссылку в письме, чтобы подтвердить email и активировать аккаунт.
                  </p>
                </div>
                
                <button
                  type="button"
                  onClick={() => { 
                    setShowRegister(false); 
                    setRegisterSuccess(false);
                    setRegisterData({ email: '', name: '', password: '', confirmPassword: '' });
                    setShowLogin(true);
                  }}
                  className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all"
                >
                  🔐 Перейти ко входу
                </button>
                
                <p className="text-center text-gray-500 text-sm">
                  Не получили письмо? Проверьте папку "Спам"
                </p>
              </div>
            ) : (
            <form onSubmit={handleRegister} className="space-y-4" autoComplete="on">
              <input type="text" name="username" autoComplete="username" className="hidden" />
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Имя</label>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  placeholder="Как к вам обращаться?"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  placeholder="example@mail.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Пароль</label>
                <input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Минимум 8 символов"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Подтвердите пароль</label>
                <input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  placeholder="Повторите пароль"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              {registerError && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{registerError}</p>
                  {registerError.includes('уже зарегистрирован') && (
                    <button 
                      type="button"
                      onClick={() => { setShowRegister(false); setShowLogin(true); }}
                      className="text-brand-500 hover:text-brand-400 text-sm mt-2 underline"
                    >
                      Войти в существующий аккаунт
                    </button>
                  )}
                </div>
              )}
              
              <button
                type="submit"
                disabled={registerLoading}
                className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all disabled:opacity-50"
              >
                {registerLoading ? '⏳ Создаём аккаунт...' : '🚀 Создать аккаунт'}
              </button>
              
              <p className="text-center text-gray-500 text-sm">
                Уже есть аккаунт?{' '}
                <button type="button" onClick={() => { setShowRegister(false); setShowLogin(true); }} className="text-purple-400 hover:text-purple-300">
                  Войти
                </button>
              </p>
            </form>
            )}
          </Modal>
        )}

        {/* Вход */}
        {showLogin && (
          <Modal onClose={() => setShowLogin(false)}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Вход</h2>
            <p className="text-gray-400 mb-6">С возвращением!</p>
            
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="on">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  placeholder="example@mail.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Пароль</label>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Ваш пароль"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              {emailConfirmedSuccess && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <p className="text-green-400 text-sm">✅ Email успешно подтверждён! Теперь вы можете войти.</p>
                </div>
              )}

              {loginError && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{loginError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all disabled:opacity-50"
              >
                {loginLoading ? '⏳ Входим...' : '🔐 Войти'}
              </button>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setShowLogin(false); setShowForgotPassword(true); }}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Забыли пароль?
                </button>
              </div>
              
              <p className="text-center text-gray-500 text-sm">
                Нет аккаунта?{' '}
                <button type="button" onClick={() => { setShowLogin(false); setShowRegister(true); }} className="text-purple-400 hover:text-purple-300">
                  Зарегистрироваться
                </button>
              </p>
            </form>
          </Modal>
        )}

        {/* Сброс пароля */}
        {showForgotPassword && (
          <Modal onClose={() => { setShowForgotPassword(false); setForgotPasswordEmail(''); setForgotPasswordError(''); setForgotPasswordSuccess(false); }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Восстановление пароля</h2>
            <p className="text-gray-400 mb-6">Введите ваш email, и мы отправим ссылку для сброса пароля</p>
            
            {forgotPasswordSuccess ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <p className="text-green-400 text-sm">
                    ✅ Письмо отправлено! Проверьте ваш email и следуйте инструкциям для сброса пароля.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setShowLogin(true); }}
                  className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all"
                >
                  Вернуться ко входу
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4" autoComplete="on">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="example@mail.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              {forgotPasswordError && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{forgotPasswordError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={forgotPasswordLoading}
                className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all disabled:opacity-50"
              >
                {forgotPasswordLoading ? '⏳ Отправляем...' : '📧 Отправить ссылку'}
              </button>
                
                <p className="text-center text-gray-500 text-sm">
                  Вспомнили пароль?{' '}
                  <button type="button" onClick={() => { setShowForgotPassword(false); setShowLogin(true); }} className="text-purple-400 hover:text-purple-300">
                    Войти
                  </button>
                </p>
              </form>
            )}
          </Modal>
        )}

        {/* Установка нового пароля (после сброса) */}
        {showResetPassword && (
          <Modal onClose={() => { 
            setShowResetPassword(false); 
            setResetPasswordData({ password: '', confirmPassword: '' }); 
            setResetPasswordError(''); 
            setResetPasswordSuccess(false);
            setResetPasswordEmail('');
            // Выходим из recovery-сессии при закрытии окна
            if (supabase) supabase.auth.signOut();
            setShowLogin(true); // Открываем окно логина
          }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Установка нового пароля</h2>
            <p className="text-gray-400 mb-6">Введите новый пароль для вашего аккаунта</p>
            
            {resetPasswordSuccess ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <p className="text-green-400 text-sm">
                    ✅ Пароль успешно изменён! Выполняем вход...
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4" autoComplete="on">
                {/* Отображаем email пользователя */}
                {resetPasswordEmail && (
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Email</label>
                    <div className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400">
                      {resetPasswordEmail}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Новый пароль</label>
                  <div className="relative">
                    <input
                      type={showResetPasswordField ? 'text' : 'password'}
                      name="new-password"
                      autoComplete="new-password"
                      value={resetPasswordData.password}
                      onChange={(e) => setResetPasswordData({ ...resetPasswordData, password: e.target.value })}
                      placeholder="Минимум 8 символов"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPasswordField(!showResetPasswordField)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      {showResetPasswordField ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Подтвердите пароль</label>
                  <input
                    type={showResetPasswordField ? 'text' : 'password'}
                    name="confirm-new-password"
                    autoComplete="new-password"
                    value={resetPasswordData.confirmPassword}
                    onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                    placeholder="Повторите пароль"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                />
              </div>
              
              {resetPasswordError && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{resetPasswordError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={resetPasswordLoading}
                className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all disabled:opacity-50"
              >
                {resetPasswordLoading ? '⏳ Сохраняем...' : '🚀 Сохранить и войти'}
              </button>
                
                <p className="text-center text-gray-500 text-sm">
                  Передумали?{' '}
                  <button 
                    type="button" 
                    onClick={() => { 
                      setShowResetPassword(false);
                      setResetPasswordEmail('');
                      // Выходим из recovery-сессии
                      if (supabase) supabase.auth.signOut();
                      setShowLogin(true); 
                    }} 
                    className="text-purple-400 hover:text-purple-300"
                  >
                    Отмена
                  </button>
                </p>
              </form>
            )}
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}
